<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-container">
      <div class="modal-header">
        <button
          type="button"
          class="modal-close-button"
          @click="close"
          aria-label="Close image validation"
        >
          &times;
        </button>
        <h3>Image Validation Result</h3>
      </div>
      <div class="modal-body">
        <div v-if="!result" class="loading-state">
          <p>Analyzing image...</p>
        </div>
        <div v-else class="result-details">
          <div class="summary-card" :class="summaryStatus">
            <div class="summary-icon">{{ summaryIcon }}</div>
            <div class="summary-text">
              <h4>{{ summaryTitle }}<span v-if="result && result.assetType"> for {{ result.assetType }}</span></h4>
              <p>{{ summaryMessage }}</p>
            </div>
          </div>

          <div class="details-grid">
            <!-- Confidence Score -->
            <div class="detail-item">
              <h5>Confidence Score</h5>
              <p class="confidence-score">{{ result.confidence?.toFixed(1) ?? 'N/A' }}%</p>
            </div>

            <!-- Moderation Labels -->
            <div class="detail-item">
              <h5>Moderation Labels</h5>
              <ul v-if="result.moderationLabels && result.moderationLabels.length > 0">
                <li v-for="label in result.moderationLabels" :key="label">{{ label }}</li>
              </ul>
              <p v-else>✅ No issues found.</p>
            </div>

            <!-- Detected Labels -->
            <div class="detail-item full-width">
              <button
                type="button"
                class="disclosure-header"
                :aria-expanded="showDetectedContent"
                @click="showDetectedContent = !showDetectedContent"
              >
                <span>Detected Content</span>
                <span class="disclosure-chevron" :class="{ open: showDetectedContent }">▾</span>
              </button>
              <div v-show="showDetectedContent" class="disclosure-body">
                <div v-if="result.labels && result.labels.length > 0" class="tag-list">
                  <span v-for="label in result.labels" :key="label" class="tag">{{ label }}</span>
                </div>
                <p v-else>No specific content labels detected.</p>
              </div>
            </div>
          </div>
          
          <!-- Error Info -->
          <div v-if="result.errorInfo" class="error-info">
            <h5>Analysis Warning</h5>
            <p><strong>{{ result.errorInfo.errorType }}:</strong> {{ result.errorInfo.userMessage }}</p>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="action-button" @click="close">
          <span v-if="result && !result.isValid">Reupload Asset</span>
          <span v-else>Acknowledge</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType, computed, ref } from 'vue';

export default defineComponent({
  name: 'RekognitionModal',
  props: {
    result: {
      type: Object as PropType<any | null>,
      default: null,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const showDetectedContent = ref(false);

    const close = () => {
      emit('close');
    };

    const summaryStatus = computed(() => {
      if (!props.result) return 'analyzing';
      if (props.result.errorInfo) return 'warning';
      if (!props.result.isValid || (props.result.moderationLabels && props.result.moderationLabels.length > 0)) {
        return 'failed';
      }
      return 'success';
    });

    const summaryIcon = computed(() => {
      const statusMap: { [key: string]: string } = {
        success: '✅',
        warning: '⚠️',
        failed: '❌',
        analyzing: '🤔',
      };
      return statusMap[summaryStatus.value];
    });

    const summaryTitle = computed(() => {
      const statusMap: { [key: string]: string } = {
        success: 'Validation Passed',
        warning: 'Analysis Incomplete',
        failed: 'Validation Failed',
        analyzing: 'Analyzing...',
      };
      return statusMap[summaryStatus.value];
    });
    
    const summaryMessage = computed(() => {
        const statusMap: { [key: string]: string } = {
            success: 'The image meets the required standards.',
            warning: 'Could not fully analyze the image, Please reupload the image/s that failed validation.',
            failed: 'The image contains inappropriate content and has been rejected.',
            analyzing: 'Please wait while we analyze your image.',
        };
        return statusMap[summaryStatus.value];
    });

    return {
      close,
      showDetectedContent,
      summaryStatus,
      summaryIcon,
      summaryTitle,
      summaryMessage,
    };
  },
});
</script>

<style scoped>
.modal-overlay {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
}

.summary-card.success {
  background-color: rgba(40, 167, 69, 0.1);
  border-left: 5px solid #28a745;
}

.summary-card.warning {
  background-color: rgba(255, 193, 7, 0.1);
  border-left: 5px solid #ffc107;
}

.summary-card.failed {
  background-color: rgba(220, 53, 69, 0.1);
  border-left: 5px solid #dc3545;
}

.summary-icon {
  font-size: 2rem;
}

.summary-text h4 {
  margin: 0 0 0.25rem 0;
  color: var(--color-heading);
}

.summary-text p {
  margin: 0;
  color: var(--color-text);
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
}

.detail-item {
  background-color: var(--color-background-soft);
  padding: 1rem;
  border-radius: 0.5rem;
}

.detail-item.full-width {
  grid-column: span 2;
}

.detail-item h5 {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  color: var(--color-heading);
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 0.5rem;
}

.detail-item p {
  margin: 0;
}

.confidence-score {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--vt-c-accent);
}

.detail-item ul {
  padding-left: 1.25rem;
  margin: 0;
}

.disclosure-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: auto;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 0.625rem;
  padding: 0.7rem 0.875rem;
  margin: 0;
  box-shadow: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  color: var(--vt-c-accent);
}

.disclosure-header:hover {
  transform: none;
  border-color: var(--color-border-hover);
  box-shadow: none;
}

.disclosure-chevron {
  display: inline-block;
  transition: transform 0.2s ease;
  font-size: 1rem;
  color: var(--vt-c-accent);
}

.disclosure-chevron.open {
  transform: rotate(180deg);
}

.disclosure-body {
  margin-top: 0.75rem;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag {
  background-color: var(--color-background-mute);
  padding: 0.25rem 0.625rem;
  border-radius: 0.75rem;
  font-size: 0.85rem;
}

.error-info {
  margin-top: 1.25rem;
  padding: 1rem;
  background-color: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 0.5rem;
}

.error-info h5 {
  margin: 0 0 0.5rem 0;
}

.error-info p {
  margin: 0;
}

.action-button {
  background-color: var(--vt-c-accent);
  color: var(--color-background);
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  width: 100%;
}
</style>
